import { AfterViewInit, Component, ElementRef, forwardRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { UserActionBaseComponent } from '../userAction-base.component';
import { Catalog } from '../../../../Model/Catalog/Catalog';
import { FieldListInterface } from '../../../../Model/ui/FieldListInterface';
import { TranslateService } from '@ngx-translate/core';
import { CatalogService } from 'app/Services/CatalogManagement/catalog.service';
import { MacroService } from 'app/Services/CatalogManagement/macro.service';
import { LoginService } from 'app/Services/login.service';
import { SystemService } from 'app/Services/system.service';
import { TemplateService } from 'app/Services/template.service';
import { UpdateTemplateService } from 'app/Services/updateTemplate.service';

@Component({
    selector: 'userAction-compareCatalogExcelExport',
    templateUrl: 'userAction-compareCatalogExcelExport.component.html',
    providers: [
        { provide: UserActionBaseComponent, useExisting: forwardRef(() => UserActionCompareCatalogExcelExportComponent) }
    ],
    standalone: false
})
export class UserActionCompareCatalogExcelExportComponent extends UserActionBaseComponent implements AfterViewInit, OnDestroy {

  _sourceCompareCatalog: string = '';
  private pendingSourceCompareCatalog: string = null;
  private fixedSourceCompareCatalog: Catalog = null;
  selectedTemplateId: string = '';
  exportChangeLog: boolean = false;
  catalogs: Catalog[];

  useFieldList: boolean = false;
  compareModeOptions: { value: boolean; text: string }[];
  fieldListContainer: FieldListInterface = { fields: ['SUPPLIER_PID'] };
  fieldGroups: string[] = [];

  @ViewChild('fieldListWrapper') fieldListWrapper: ElementRef;
  fieldListHeight: number = 300;
  private _resizeObserver?: ResizeObserver;

  constructor(
    public loginService: LoginService,
    public macroService: MacroService,
    public translate: TranslateService,
    public catalogService: CatalogService,
    public systemService: SystemService,
    public templateService: TemplateService,
    public updateTemplateService: UpdateTemplateService,
    public elementRef: ElementRef
  ) {
    super(loginService, macroService);
    this.compareModeOptions = [
      { value: false, text: this.translate.instant('Export-Template') },
      { value: true, text: this.translate.instant('Feldliste') }
    ];
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.catalogService
      .getCatalogs(this.loginService.currentUser.customerId)
      .subscribe((result: Catalog[]) => {
        this.catalogs = result.filter((c) => !c.isVirtual);
        this.applySourceCompareCatalog();
      });
    this.templateService.getExportTemplates();
    this.catalogService.loadFields();
    this.fieldGroups = this.updateTemplateService.GetFieldGroups();
  }

  ngAfterViewInit(): void {
    this.updateFieldListHeight();

    const scrollHost = (this.elementRef.nativeElement as HTMLElement).closest('.dx-scrollview');
    if (scrollHost && typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this.updateFieldListHeight());
      this._resizeObserver.observe(scrollHost);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._resizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateFieldListHeight();
  }

  updateFieldListHeight(): void {
    setTimeout(() => {
      const element = this.fieldListWrapper?.nativeElement as HTMLElement;
      if (!element) return;

      const top = element.getBoundingClientRect().top;
      const scrollHost = element.closest('.dx-scrollview') as HTMLElement;
      const bottom = scrollHost ? scrollHost.getBoundingClientRect().bottom : window.innerHeight;

      this.fieldListHeight = Math.max(150, bottom - top - 8);
    });
  }

  get sourceCompareCatalog(): string {
    return this._sourceCompareCatalog;
  }

  set sourceCompareCatalog(sourceCompareCatalog: string) {
    this._sourceCompareCatalog = sourceCompareCatalog;
  }

  get isSourceCompareCatalogFixed(): boolean {
    return this.fixedSourceCompareCatalog != null;
  }

  private applySourceCompareCatalog(): void {
    if (this.fixedSourceCompareCatalog) {
      this.catalogs = [this.fixedSourceCompareCatalog];
    }
    if (this.pendingSourceCompareCatalog === null || !this.catalogs) {
      return;
    }
    this._sourceCompareCatalog = this.pendingSourceCompareCatalog;
    this.pendingSourceCompareCatalog = null;
  }

  async canStartMacro(): Promise<boolean> {
    if (this.sourceCompareCatalog == '') {
      this.systemService.notifyWarning(this.translate.instant('Bitte Katalog auswählen.'));
      return false;
    }
    if (this.sourceCompareCatalog == this.macroService.targetCatalogId) {
      this.systemService.notifyWarning(this.translate.instant('Katalog kann nicht mit sich selbst verglichen werden.'));
      return false;
    }
    if (this.useFieldList) {
      if ((this.fieldListContainer.fields ?? []).length == 0) {
        this.systemService.notifyWarning(this.translate.instant('Bitte mindestens ein Feld auswählen.'));
        return false;
      }
    } else if (this.selectedTemplateId == '') {
      this.systemService.notifyWarning(this.translate.instant('Bitte Export-Template auswählen.'));
      return false;
    }
    return true;
  }

  public createParameterStringAsJsonString(): string {
    return JSON.stringify({
      sourceCompareCatalog: this.sourceCompareCatalog,
      useFieldList: this.useFieldList,
      templateId: this.selectedTemplateId,
      fields: this.fieldListContainer.fields,
      exportChangeLog: this.exportChangeLog,
    });
  }

  public parseParameter(parameter: string) {
    if (!parameter) return;
    try {
      const parsed = JSON.parse(parameter);
      if (parsed) {
        this.pendingSourceCompareCatalog = parsed.sourceCompareCatalog ?? '';
        this.fixedSourceCompareCatalog = parsed.sourceCompareCatalogName
          ? ({ id: parsed.sourceCompareCatalog, catalogName: parsed.sourceCompareCatalogName } as Catalog)
          : null;
        this.applySourceCompareCatalog();
        this.useFieldList = parsed.useFieldList ?? false;
        this.selectedTemplateId = parsed.templateId ?? '';
        this.fieldListContainer = { fields: parsed.fields ?? ['SUPPLIER_PID'] };
        this.exportChangeLog = parsed.exportChangeLog ?? false;
      }
    } catch (error) {
      console.error('Invalid JSON string provided:', error);
    }
  }
}
